import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { ApolloError } from '@apollo/client/errors';
import { ToastType } from '@alefeducation/ui-elements-library-core';
import { GET_ORGANISATIONS, Organisation } from '../../apollo/queries';
import { UPDATE_ORGANISATION } from '../../apollo/mutations';
import { injectAutoHideToastOfType } from '../../utils/Toast';
import {
  ORG_SUCCESSFULLY_ACTIVATED_MESSAGE,
  ORG_SUCCESSFULLY_DEACTIVATED_MESSAGE,
  ORGNISATIONS_PER_PAGE,
  SOMETHING_WENT_WRONG_MESSAGE,
} from '../../utils/constants';
import usePagination from '../../components/Pagination/hooks/usePagination';
import appLocalStorage from '../../utils/AppLocalStorage';

interface GetORGData {
  organisationsPage: {
    organisations?: Organisation[];
    pageNumber: number;
    pageSize: number;
    totalCount: number;
  };
}

const useOrganisations = () => {
  const {
    queryData: {
      loading: isOrgLoading,
      error: orgError,
      data: orgData,
      refetch: refetchOrganisations,
    },
    pageData: { currentPage, search, totalPages, pageStats },
    actions: { setCurrentPage, setSearch, onSearch },
  } = usePagination<GetORGData>({
    query: GET_ORGANISATIONS,
    responseField: 'organisationsPage',
    pageSize: ORGNISATIONS_PER_PAGE,
  });

  const [updateStatus] = useMutation(UPDATE_ORGANISATION, {
    onCompleted: () => {
      const title = organisationInEdit?.isActive
        ? ORG_SUCCESSFULLY_DEACTIVATED_MESSAGE
        : ORG_SUCCESSFULLY_ACTIVATED_MESSAGE;

      injectAutoHideToastOfType(
        'organisations-page-toast',
        title,
        ToastType.SUCCESS,
      );
      refetchOrganisations();
      setOrganisationInEdit(undefined);
    },
    onError: (error: ApolloError) => {
      injectAutoHideToastOfType(
        'organisations-page-toast',
        SOMETHING_WENT_WRONG_MESSAGE,
        ToastType.ERROR,
      );
      console.log(error);
    },
  });

  const [organisationInEdit, setOrganisationInEdit] = useState<
    Organisation | undefined
  >(undefined);

  const [showEditModal, setEditModal] = useState(false);
  const [showStatusModal, setStatusModal] = useState(false);

  const onAddOrgClick = () => {
    setOrganisationInEdit(undefined);
    setEditModal(true);
  };

  const onEditClick = (organisation: Organisation) => {
    setOrganisationInEdit(organisation);
    setEditModal(true);
  };

  const onStatusClick = (organisation: Organisation) => {
    setOrganisationInEdit(organisation);
    setStatusModal(true);
  };

  const onStatusUpdateConfirmation = () => {
    setStatusModal(false);

    const { name, email, isActive, country, uuid, tenantCode } =
      organisationInEdit!;

    updateStatus({
      variables: {
        uuid: uuid,
        orgSchema: { isActive: !isActive, email, name, country, tenantCode },
      },
    });
  };

  return {
    orgResponse: {
      isLoading: isOrgLoading,
      error: orgError,
      organisationsPage: orgData?.organisationsPage || {
        organisations: [],
        pageNumber: 0,
        totalCount: 1,
      },
    },
    modal: {
      search,
      currentPage,
      totalPages,
      showEditModal,
      setEditModal,
      showStatusModal,
      setStatusModal,
      onStatusUpdateConfirmation,
      onSearchDebounce: onSearch,
      setSearch,
      setCurrentPage,
    },
    menuActions: {
      onEditClick,
      onStatusClick,
    },
    onAddOrgClick,
    isViewOnlyRole: appLocalStorage.isViewOnlyRole(),
    onStatusUpdateConfirmation,
    organisationInEdit,
    pageStats,
  };
};

export default useOrganisations;
